import React, { useEffect, useState } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

// Confetti animation for tab
const confettiAnimation = keyframes`
  0% {
    transform: translateY(-20px) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100px) rotate(360deg);
    opacity: 0;
  }
`;

// Success icon bounce animation
const bounceAnimation = keyframes`
  0% {
    transform: translateY(0) scale(0.8) rotate(0deg);
    opacity: 0;
  }
  20% {
    transform: translateY(-15px) scale(1.1) rotate(5deg);
    opacity: 1;
  }
  40% {
    transform: translateY(-8px) scale(1.05) rotate(-2deg);
  }
  60% {
    transform: translateY(-4px) scale(1.02) rotate(1deg);
  }
  80% {
    transform: translateY(-2px) scale(1.01) rotate(0deg);
  }
  100% {
    transform: translateY(0) scale(1) rotate(0deg);
  }
`;

// Pulse animation for the success circle
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.4;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
`;

// Text slide-in animation
const slideInAnimation = keyframes`
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Sparkle animation
const sparkleAnimation = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Celebration banner animation
const bannerSlideIn = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const CelebrationAnimation = ({ isVisible = false, onComplete = () => {} }) => {
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShowAnimation(true);
      // Auto-hide after 3 seconds
      const timer = setTimeout(() => {
        setShowAnimation(false);
        onComplete();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isVisible, onComplete]);

  if (!showAnimation) return null;

  // Generate confetti pieces (fewer for tab)
  const confettiPieces = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    delay: Math.random() * 1,
    duration: 2 + Math.random() * 1,
    left: Math.random() * 100,
    color: ["#4CAF50", "#2196F3", "#FF9800", "#E91E63", "#9C27B0", "#00BCD4"][
      Math.floor(Math.random() * 6)
    ],
  }));

  // Generate sparkle positions (fewer for tab)
  const sparkles = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    delay: Math.random() * 1,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 4 + 2,
  }));

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "120px",
        overflow: "hidden",
        background: "linear-gradient(135deg, #E8F5E8 0%, #F1F8E9 100%)",
        borderRadius: 2,
        border: "2px solid #4CAF50",
        mb: 2,
        animation: `${bannerSlideIn} 0.6s ease-out`,
      }}
    >
      {/* Confetti */}
      {confettiPieces.map((piece) => (
        <Box
          key={piece.id}
          sx={{
            position: "absolute",
            left: `${piece.left}%`,
            top: "-10px",
            width: "4px",
            height: "4px",
            backgroundColor: piece.color,
            borderRadius: "50%",
            animation: `${confettiAnimation} ${piece.duration}s ease-out ${piece.delay}s infinite`,
          }}
        />
      ))}

      {/* Sparkles */}
      {sparkles.map((sparkle) => (
        <Box
          key={sparkle.id}
          sx={{
            position: "absolute",
            left: `${sparkle.left}%`,
            top: `${sparkle.top}%`,
            width: `${sparkle.size}px`,
            height: `${sparkle.size}px`,
            backgroundColor: "#FFD700",
            borderRadius: "50%",
            animation: `${sparkleAnimation} 1.5s ease-in-out ${sparkle.delay}s infinite`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "1px",
              height: "1px",
              backgroundColor: "#FFD700",
              borderRadius: "50%",
              transform: "translate(-50%, -50%)",
            },
          }}
        />
      ))}

      {/* Success Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Success Icon */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "#4CAF50",
            boxShadow:
              "0 4px 20px rgba(76, 175, 80, 0.3), 0 0 0 0 rgba(76, 175, 80, 0.4)",
            animation: `${bounceAnimation} 1s ease-in-out`,
            mb: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              border: "3px solid #4CAF50",
              animation: `${pulseAnimation} 2s ease-out infinite`,
            },
            "&::after": {
              content: '""',
              position: "absolute",
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              border: "2px solid rgba(76, 175, 80, 0.3)",
              animation: `${pulseAnimation} 2s ease-out infinite 0.5s`,
            },
          }}
        >
          {/* Clear Checkmark */}
          <CheckRoundedIcon
            sx={{
              width: "32px",
              height: "32px",
              color: "white",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
            }}
          />
        </Box>

        {/* Success Message */}
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: "bold",
            color: "#2E7D32",
            textAlign: "center",
            animation: `${slideInAnimation} 0.6s ease-out 0.3s both`,
            mb: 0.5,
          }}
        >
          🎉 All Test Cases Passed! 🎉
        </Typography>

        <Typography
          sx={{
            fontSize: "12px",
            color: "#4CAF50",
            textAlign: "center",
            animation: `${slideInAnimation} 0.6s ease-out 0.5s both`,
            fontWeight: 500,
          }}
        >
          Excellent work! Your solution is working perfectly.
        </Typography>
      </Box>
    </Box>
  );
};

export default CelebrationAnimation;
